package com.havn.app.features.agency.presentation

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.clickable
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnCardBorder
import com.havn.app.core.designsystem.HavnGold
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnListFilterChip
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnSearchField
import com.havn.app.core.designsystem.HavnSpacing
import com.havn.app.core.designsystem.HavnSurface
import com.havn.app.core.designsystem.HavnSurfaceElevated
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.HavnTextMuted
import com.havn.app.core.designsystem.HavnTextPrimary
import com.havn.app.core.designsystem.HavnTextSecondary
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.HavnPickedPhoto
import com.havn.app.core.media.HavnPhotoPicker
import com.havn.app.core.media.havnAgencyMediaPreviewSource
import com.havn.app.features.agency.domain.Agency
import com.havn.app.features.agency.domain.AgencyMember
import com.havn.app.features.agency.domain.AgencyMemberStatus
import com.havn.app.features.agency.domain.AgencyRole
import com.havn.app.ui.theme.LocalHavnThemeTokens

@Composable
fun AgencyScreen(
    viewModel: AgencyViewModel,
    photoPicker: HavnPhotoPicker,
    propertyCount: Int = 0,
    leadCount: Int = 0,
    recentLeadTitle: String? = null,
    recentPropertyTitle: String? = null,
    onBack: () -> Unit = {},
    onManageAgency: () -> Unit = {}
) {
    val state by viewModel.uiState.collectAsState()
    val theme = LocalHavnThemeTokens.current
    val selectedAgency = state.selectedAgency
    var showAgents by remember(selectedAgency?.id) { mutableStateOf(false) }

    if (selectedAgency != null) {
        Crossfade(targetState = showAgents) { agentsVisible ->
            if (agentsVisible) {
                AgencyAgentsScreen(
                    agency = selectedAgency,
                    members = state.members,
                    inviteEmail = state.inviteEmail,
                    inviteRole = state.inviteRole,
                    isLoading = state.isLoadingMembers,
                    isInviting = state.isInviting,
                    deletingInviteId = state.isDeletingInviteId,
                    updatingRoleAgentId = state.updatingRoleAgentId,
                    onInviteEmailChanged = viewModel::onInviteEmailChanged,
                    onInviteRoleChanged = viewModel::onInviteRoleChanged,
                    onInvite = viewModel::inviteAgent,
                    onDeleteInvite = viewModel::deleteInvite,
                    onUpdateMemberRole = viewModel::updateMemberRole,
                    onRemoveMemberAccess = viewModel::removeMemberAccess,
                    onBack = { showAgents = false }
                )
            } else {
                AgencyOverviewScreen(
                    state = state,
                    agency = selectedAgency,
                    propertyCount = propertyCount,
                    leadCount = leadCount,
                    recentLeadTitle = recentLeadTitle,
                    recentPropertyTitle = recentPropertyTitle,
                    agencyBrandingName = state.agencyBrandingName,
                    isSavingAgencyBranding = state.isSavingAgencyBranding,
                    isUploadingAgencyLogo = state.isUploadingAgencyLogo,
                    photoPicker = photoPicker,
                    onSelectAgency = viewModel::selectAgency,
                    onAgencyBrandingNameChanged = viewModel::onAgencyBrandingNameChanged,
                    onSaveAgencyBranding = viewModel::saveAgencyBranding,
                    onUploadAgencyLogo = viewModel::uploadAgencyLogo,
                    onToggleActLikeAgent = viewModel::toggleActLikeAgent,
                    onOpenAgents = { showAgents = true },
                    onDismissError = viewModel::clearError,
                    onBack = onBack
                )
            }
        }
    } else {
        AgencySetupScreen(
            state = state,
            viewModel = viewModel,
            onBack = onBack
        )
    }
}

@Composable
private fun AgencyOverviewScreen(
    state: AgencyUiState,
    agency: Agency,
    propertyCount: Int,
    leadCount: Int,
    recentLeadTitle: String?,
    recentPropertyTitle: String?,
    agencyBrandingName: String,
    isSavingAgencyBranding: Boolean,
    isUploadingAgencyLogo: Boolean,
    photoPicker: HavnPhotoPicker,
    onSelectAgency: (String) -> Unit,
    onAgencyBrandingNameChanged: (String) -> Unit,
    onSaveAgencyBranding: () -> Unit,
    onUploadAgencyLogo: (HavnPickedPhoto?) -> Unit,
    onToggleActLikeAgent: () -> Unit,
    onOpenAgents: () -> Unit,
    onDismissError: () -> Unit,
    onBack: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    state.error?.let { error ->
        AgencyErrorDialog(error = error, onDismiss = onDismissError)
    }
    HavnScreenFrame(
        eyebrow = "",
        title = strings.commonAgency,
        subtitle = "",
        backLabel = strings.commonToday,
        onBack = onBack
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            AgencySwitcherCard(
                agencies = state.agencies,
                selectedAgencyId = state.selectedAgencyId,
                onSelectAgency = onSelectAgency
            )

            AgencyHeroCard(
                agency = agency
            )

            if (agency.role.canManageBranding) {
                AgencyBrandingCard(
                    agency = agency,
                    name = agencyBrandingName.ifBlank { agency.name },
                    isSaving = isSavingAgencyBranding,
                    isUploadingLogo = isUploadingAgencyLogo,
                    photoPicker = photoPicker,
                    onNameChanged = onAgencyBrandingNameChanged,
                    onSave = onSaveAgencyBranding,
                    onLogoPicked = onUploadAgencyLogo
                )
            }

            AgencyRoleCard(
                role = agency.role,
                effectiveRole = state.effectiveSelectedAgencyRole ?: agency.role,
                canActLikeAgent = state.canActLikeAgent,
                isActingLikeAgent = state.isActingLikeAgent && state.canActLikeAgent,
                onToggleActLikeAgent = onToggleActLikeAgent
            )

            AgencyTeamCard(
                members = state.members,
                isLoading = state.isLoadingMembers,
                onOpenAgents = onOpenAgents
            )

            AgencyStatsCard(
                properties = propertyCount,
                agents = agency.memberCount,
                leads = leadCount
            )

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    strings.agencyRecentActivity,
                    modifier = Modifier.weight(1f),
                    color = theme.primaryText,
                    style = MaterialTheme.typography.titleMedium
                )
                Text(
                    strings.seeAll,
                    color = theme.mutedText,
                    style = MaterialTheme.typography.labelLarge
                )
            }

            AgencyActivityCard(
                items = listOf(
                    AgencyActivityItem(
                        icon = HavnIconKey.Note,
                        title = strings.agencyActivityNewLead,
                        subtitle = recentLeadTitle ?: "123 Main Street Open House",
                        time = "2m ago"
                    ),
                    AgencyActivityItem(
                        icon = HavnIconKey.User,
                        title = strings.agencyActivityInvitationAccepted,
                        subtitle = strings.agencyActivityAgentJoined,
                        time = "1h ago"
                    ),
                    AgencyActivityItem(
                        icon = HavnIconKey.Reports,
                        title = strings.agencyActivitySellerReport,
                        subtitle = recentPropertyTitle ?: "456 Park Avenue",
                        time = "3h ago"
                    )
                )
            )
        }
    }
}

@Composable
private fun AgencySwitcherCard(
    agencies: List<Agency>,
    selectedAgencyId: String?,
    onSelectAgency: (String) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Text(
                strings.agencyWorkspaceTitle,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold
            )
            Text(
                strings.agencyWorkspaceBody,
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodyMedium
            )
            agencies.forEach { agency ->
                AgencySwitcherRow(
                    agency = agency,
                    selected = agency.id == selectedAgencyId,
                    onClick = { onSelectAgency(agency.id) }
                )
            }
        }
    }
}

@Composable
private fun AgencySwitcherRow(
    agency: Agency,
    selected: Boolean,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val shape = RoundedCornerShape(16.dp)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(shape)
            .background(if (selected) theme.primaryAccent.copy(alpha = 0.14f) else theme.surface.copy(alpha = 0.5f))
            .border(
                BorderStroke(1.dp, if (selected) theme.primaryAccent.copy(alpha = 0.72f) else theme.border.copy(alpha = 0.62f)),
                shape
            )
            .clickable(onClick = onClick)
            .padding(HavnSpacing.md),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)
    ) {
        AgencyLogo(name = agency.name, iconUrl = agency.iconUrl, modifier = Modifier.size(44.dp))
        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.spacedBy(3.dp)
        ) {
            Text(
                agency.name,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold
            )
            Text(
                "${agency.role.localizedDisplayName(strings)} - ${agency.memberCount} ${strings.members}",
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodySmall
            )
        }
        HavnIcon(
            icon = if (selected) HavnIconKey.Check else HavnIconKey.ArrowRight,
            contentDescription = null,
            modifier = Modifier.size(20.dp),
            tint = if (selected) theme.primaryAccent else theme.mutedText
        )
    }
}

@Composable
private fun AgencySetupScreen(
    state: AgencyUiState,
    viewModel: AgencyViewModel,
    onBack: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    state.error?.let { error ->
        AgencyErrorDialog(error = error, onDismiss = viewModel::clearError)
    }
    HavnScreenFrame(
        eyebrow = strings.commonAgency,
        title = state.workspaceName,
        subtitle = strings.agencySetupSubtitle,
        backLabel = strings.commonToday,
        onBack = onBack
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            EntitlementCard(state)

            if (state.entitlement.canCreateAgency && state.agencies.isEmpty()) {
                HavnCard {
                    Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                        Text(strings.createAgency, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                        HavnTextField(
                            value = state.newAgencyName,
                            onValueChange = viewModel::onAgencyNameChanged,
                            label = strings.agencyName,
                            leadingIcon = HavnIconKey.Home
                        )
                        HavnButton(
                            text = "+ ${strings.createAgency}",
                            onClick = viewModel::createAgency,
                            loading = state.isSaving,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
            }

            if (state.agencies.isEmpty()) {
                EmptyState(
                    title = if (state.entitlement.isProAgent) strings.agencySoloActiveTitle else strings.agencyProRequiredTitle,
                    body = if (state.entitlement.isProAgent) {
                        strings.agencyCreateOrAcceptInviteBody
                    } else {
                        strings.agencyProUnlockBody
                    }
                )
            } else {
                state.agencies.forEach { agency ->
                    AgencyCard(
                        agency = agency,
                        selected = agency.id == state.selectedAgencyId,
                        onClick = { viewModel.selectAgency(agency.id) }
                    )
                }
            }
        }
    }
}

@Composable
private fun AgencyHeroCard(
    agency: Agency
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)
        ) {
            AgencyLogo(
                name = agency.name,
                iconUrl = agency.iconUrl,
                modifier = Modifier.size(72.dp)
            )
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                Text(
                    agency.name,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.SemiBold
                )
                Text(
                    strings.realEstate,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
                Surface(
                    shape = RoundedCornerShape(999.dp),
                    color = theme.primaryAccent.copy(alpha = 0.12f),
                    border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.6f))
                ) {
                    Text(
                        strings.proAgency,
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 5.dp),
                        color = theme.primaryAccent,
                        style = MaterialTheme.typography.labelMedium
                    )
                }
            }
        }
    }
}

@Composable
private fun AgencyBrandingCard(
    agency: Agency,
    name: String,
    isSaving: Boolean,
    isUploadingLogo: Boolean,
    photoPicker: HavnPhotoPicker,
    onNameChanged: (String) -> Unit,
    onSave: () -> Unit,
    onLogoPicked: (HavnPickedPhoto?) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            Text(
                strings.agencyBrandingTitle,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.SemiBold
            )
            Text(
                strings.agencyBrandingBody,
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodyMedium
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)
            ) {
                AgencyLogo(
                    name = name.ifBlank { agency.name },
                    iconUrl = agency.iconUrl,
                    modifier = Modifier.size(76.dp)
                )
                HavnButton(
                    text = if (isUploadingLogo) strings.uploading else strings.uploadLogo,
                    onClick = { photoPicker.pick(onLogoPicked) },
                    style = HavnButtonStyle.Secondary,
                    loading = isUploadingLogo,
                    enabled = !isUploadingLogo && !isSaving,
                    leadingIcon = HavnIconKey.Plus,
                    modifier = Modifier.weight(1f)
                )
            }
            HavnTextField(
                value = name,
                onValueChange = onNameChanged,
                label = strings.agencyName,
                leadingIcon = HavnIconKey.Home,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text)
            )
            HavnButton(
                text = if (isSaving) strings.saving else strings.saveAgency,
                onClick = onSave,
                loading = isSaving,
                enabled = !isSaving && !isUploadingLogo,
                leadingIcon = HavnIconKey.Check,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun AgencyRoleCard(
    role: AgencyRole,
    effectiveRole: AgencyRole,
    canActLikeAgent: Boolean,
    isActingLikeAgent: Boolean,
    onToggleActLikeAgent: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val displayedRole = if (isActingLikeAgent) effectiveRole else role
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)
            ) {
                Surface(
                    shape = CircleShape,
                    color = theme.primaryAccent.copy(alpha = 0.14f),
                    border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.5f))
                ) {
                    HavnIcon(
                        icon = when (role) {
                            AgencyRole.Owner -> HavnIconKey.Star
                            AgencyRole.Admin -> HavnIconKey.Settings
                            AgencyRole.Agent -> HavnIconKey.User
                            AgencyRole.Assistant -> HavnIconKey.ContactCard
                            AgencyRole.Viewer -> HavnIconKey.Reports
                        },
                        contentDescription = null,
                        modifier = Modifier.padding(10.dp).size(22.dp),
                        tint = theme.primaryAccent
                    )
                }
                Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text(
                        "${strings.yourAgencyRole}: ${role.localizedDisplayName(strings)}",
                        color = theme.primaryText,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold
                    )
                    Text(
                        displayedRole.localizedShortDescription(strings),
                        color = theme.secondaryText,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                displayedRole.localizedCapabilities(strings).forEach { capability ->
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm),
                        verticalAlignment = Alignment.Top
                    ) {
                        HavnIcon(
                            icon = HavnIconKey.Check,
                            contentDescription = null,
                            modifier = Modifier.size(18.dp),
                            tint = theme.primaryAccent
                        )
                        Text(
                            capability,
                            color = theme.primaryText,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }
                }
            }
            if (canActLikeAgent) {
                ActLikeAgentButton(
                    selected = isActingLikeAgent,
                    agencyRole = role,
                    onClick = onToggleActLikeAgent
                )
            }
        }
    }
}

@Composable
private fun ActLikeAgentButton(
    selected: Boolean,
    agencyRole: AgencyRole,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val shape = RoundedCornerShape(16.dp)
    val title = when {
        !selected -> strings.actLikeAgent
        agencyRole == AgencyRole.Owner -> strings.actAsOwner
        else -> strings.actAsAdmin
    }
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clip(shape)
            .clickable(onClick = onClick),
        shape = shape,
        color = if (selected) theme.primaryAccent.copy(alpha = 0.18f) else theme.surface.copy(alpha = 0.54f),
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border.copy(alpha = 0.72f))
    ) {
        Row(
            modifier = Modifier.padding(horizontal = HavnSpacing.md, vertical = 13.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
        ) {
            HavnIcon(
                icon = if (selected) HavnIconKey.Check else HavnIconKey.User,
                contentDescription = null,
                modifier = Modifier.size(20.dp),
                tint = theme.primaryAccent
            )
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(
                    title,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.labelLarge,
                    fontWeight = FontWeight.SemiBold
                )
                Text(
                    if (selected) strings.actLikeAgentSelectedBody else strings.actLikeAgentBody,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodySmall
                )
            }
        }
    }
}

@Composable
private fun AgencyTeamCard(
    members: List<AgencyMember>,
    isLoading: Boolean,
    onOpenAgents: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val pendingCount = members.count { it.status == AgencyMemberStatus.Invited }
    val activeCount = members.count { it.status == AgencyMemberStatus.Active }
    val memberCount = activeCount + pendingCount
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)
            ) {
                Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text(
                        strings.agencyAgentsTitle,
                        color = theme.primaryText,
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.SemiBold
                    )
                    Text(
                        strings.agencyAgentsBody,
                        color = theme.secondaryText,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }

            if (isLoading) {
                Text(strings.loadingAgents, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            } else if (members.isEmpty()) {
                Text(strings.noAgencyAgentsYet, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            } else {
                Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
                    AgencySmallStat(value = activeCount.toString(), label = strings.commonActive, modifier = Modifier.weight(1f))
                    AgencySmallStat(value = pendingCount.toString(), label = AgencyMemberStatus.Invited.displayName, modifier = Modifier.weight(1f))
                    AgencySmallStat(value = memberCount.toString(), label = strings.members.toSentenceCaseLabel(), modifier = Modifier.weight(1f))
                }
            }
            HavnButton(
                text = strings.agencyAgentsTitle,
                onClick = onOpenAgents,
                style = HavnButtonStyle.Secondary,
                leadingIcon = HavnIconKey.Users,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun AgencySmallStat(
    value: String,
    label: String,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(14.dp),
        color = theme.surface.copy(alpha = 0.52f),
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.62f))
    ) {
        Column(modifier = Modifier.padding(HavnSpacing.sm), verticalArrangement = Arrangement.spacedBy(3.dp)) {
            Text(value, color = theme.primaryText, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.SemiBold)
            Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelMedium, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
    }
}

@Composable
private fun AgencyAgentsScreen(
    agency: Agency,
    members: List<AgencyMember>,
    inviteEmail: String,
    inviteRole: AgencyRole,
    isLoading: Boolean,
    isInviting: Boolean,
    deletingInviteId: String?,
    updatingRoleAgentId: String?,
    onInviteEmailChanged: (String) -> Unit,
    onInviteRoleChanged: (AgencyRole) -> Unit,
    onInvite: () -> Unit,
    onDeleteInvite: (String) -> Unit,
    onUpdateMemberRole: (String, AgencyRole) -> Unit,
    onRemoveMemberAccess: (String) -> Unit,
    onBack: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    var showInviteDialog by remember { mutableStateOf(false) }
    var query by remember { mutableStateOf("") }
    var selectedFilter by remember { mutableStateOf<AgencyAgentFilter>(AgencyAgentFilter.All) }
    var inviteRequested by remember { mutableStateOf(false) }
    val sortedMembers = remember(members) { members.sortedForAgencyAgents() }
    val filteredMembers = remember(sortedMembers, query, selectedFilter) {
        val normalizedQuery = query.trim().lowercase()
        sortedMembers.filter { member ->
            val matchesFilter = selectedFilter.matches(member)
            val matchesQuery = normalizedQuery.isBlank() ||
                member.fullName.contains(normalizedQuery, ignoreCase = true) ||
                member.email.contains(normalizedQuery, ignoreCase = true) ||
                member.role.localizedDisplayName(strings).contains(normalizedQuery, ignoreCase = true) ||
                member.status.displayName.contains(normalizedQuery, ignoreCase = true)
            matchesFilter && matchesQuery
        }
    }

    HavnScreenFrame(
        eyebrow = "",
        title = strings.agencyAgentsTitle,
        subtitle = "",
        backLabel = strings.commonAgency,
        onBack = onBack,
        headerTrailing = {
            if (agency.role.canManageMembers) {
                AgencyIconCircleButton(
                    icon = HavnIconKey.Plus,
                    contentDescription = strings.inviteAgent,
                    onClick = { showInviteDialog = true }
                )
            }
        }
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            HavnSearchField(value = query, onValueChange = { query = it }, placeholder = strings.agents)

            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
                    Box(modifier = Modifier.weight(1f)) {
                        HavnListFilterChip(
                            label = strings.commonAll,
                            selected = selectedFilter == AgencyAgentFilter.All,
                            onClick = { selectedFilter = AgencyAgentFilter.All }
                        )
                    }
                    listOf(AgencyRole.Owner, AgencyRole.Admin, null).forEach { role ->
                        Box(modifier = Modifier.weight(1f)) {
                            if (role == null) {
                                HavnListFilterChip(
                                    label = agencyInvitedFilterLabel(strings),
                                    selected = selectedFilter == AgencyAgentFilter.Invited,
                                    onClick = { selectedFilter = AgencyAgentFilter.Invited }
                                )
                            } else {
                                HavnListFilterChip(
                                    label = role.localizedDisplayName(strings),
                                    selected = selectedFilter == AgencyAgentFilter.Role(role),
                                    onClick = { selectedFilter = AgencyAgentFilter.Role(role) }
                                )
                            }
                        }
                    }
                }
                Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
                    listOf(AgencyAgentFilter.Role(AgencyRole.Agent), AgencyAgentFilter.Role(AgencyRole.Assistant), AgencyAgentFilter.Role(AgencyRole.Viewer)).forEach { filter ->
                        Box(modifier = Modifier.weight(1f)) {
                            HavnListFilterChip(
                                label = filter.label(strings),
                                selected = selectedFilter == filter,
                                onClick = { selectedFilter = filter }
                            )
                        }
                    }
                }
            }

            when {
                isLoading -> Text(strings.loadingAgents, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                members.isEmpty() -> EmptyState(title = strings.noAgencyAgentsYet, body = strings.agencyAgentsBody)
                filteredMembers.isEmpty() -> EmptyState(title = strings.noAgencyAgentsYet, body = strings.agencyAgentsBody)
                else -> filteredMembers.forEach { member ->
                    AgencyMemberRow(
                        member = member,
                        canDeleteInvite = agency.role.canManageMembers,
                        canChangeRole = agency.role.canChangeRoles,
                        isDeletingInvite = deletingInviteId == member.invitationId,
                        isUpdatingRole = updatingRoleAgentId == member.agentId,
                        onDeleteInvite = onDeleteInvite,
                        onUpdateRole = onUpdateMemberRole,
                        onRemoveAccess = onRemoveMemberAccess
                    )
                }
            }
        }
    }

    if (showInviteDialog) {
        LaunchedEffect(isInviting, inviteEmail, inviteRequested) {
            if (inviteRequested && !isInviting && inviteEmail.isBlank()) {
                inviteRequested = false
                showInviteDialog = false
            }
        }
        AgencyInviteDialog(
            email = inviteEmail,
            role = inviteRole,
            isInviting = isInviting,
            onEmailChanged = onInviteEmailChanged,
            onRoleChanged = onInviteRoleChanged,
            onDismiss = { showInviteDialog = false },
            onInvite = {
                inviteRequested = true
                onInvite()
            }
        )
    }
}

@Composable
private fun AgencyIconCircleButton(
    icon: HavnIconKey,
    contentDescription: String,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        onClick = onClick,
        modifier = Modifier.size(48.dp),
        shape = CircleShape,
        color = theme.surfaceElevated,
        contentColor = theme.primaryAccent,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Box(contentAlignment = Alignment.Center) {
            HavnIcon(icon, contentDescription = contentDescription, modifier = Modifier.size(21.dp), tint = theme.primaryAccent)
        }
    }
}

private fun List<AgencyMember>.sortedForAgencyAgents(): List<AgencyMember> = sortedWith(
    compareBy<AgencyMember> { it.fullName.lowercase() }
        .thenBy { it.email.lowercase() }
)

private sealed class AgencyAgentFilter {
    data object All : AgencyAgentFilter()
    data object Invited : AgencyAgentFilter()
    data class Role(val role: AgencyRole) : AgencyAgentFilter()

    fun matches(member: AgencyMember): Boolean = when (this) {
        All -> true
        Invited -> member.status == AgencyMemberStatus.Invited
        is Role -> member.role == role && member.status == AgencyMemberStatus.Active
    }

    fun label(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
        All -> strings.commonAll
        Invited -> agencyInvitedFilterLabel(strings)
        is Role -> role.localizedDisplayName(strings)
    }
}

private fun agencyInvitedFilterLabel(strings: com.havn.app.core.localization.HavnStrings): String = when (strings.commonAll) {
    "Všetko" -> "Pozvaní"
    "Vše" -> "Pozvaní"
    "Alle" -> "Eingeladen"
    "Todos" -> "Invitados"
    "Tout" -> "Invités"
    "Tutti" -> "Invitati"
    else -> "Invited"
}

@Composable
private fun AgencyMemberRow(
    member: AgencyMember,
    canDeleteInvite: Boolean,
    canChangeRole: Boolean,
    isDeletingInvite: Boolean,
    isUpdatingRole: Boolean,
    onDeleteInvite: (String) -> Unit,
    onUpdateRole: (String, AgencyRole) -> Unit,
    onRemoveAccess: (String) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    var showRoleDialog by remember { mutableStateOf(false) }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(theme.surface.copy(alpha = 0.52f))
            .border(BorderStroke(1.dp, theme.border.copy(alpha = 0.62f)), RoundedCornerShape(16.dp))
            .padding(HavnSpacing.md),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)
    ) {
        Surface(
            modifier = Modifier.size(44.dp),
            shape = CircleShape,
            color = theme.primaryAccent.copy(alpha = 0.16f),
            border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.42f))
        ) {
            Box(contentAlignment = Alignment.Center) {
                Text(
                    member.fullName.initials(),
                    color = theme.primaryAccent,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold
                )
            }
        }
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(3.dp)) {
            Text(
                member.fullName,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold
            )
            Text(
                member.email.ifBlank { member.status.displayName },
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodySmall
            )
        }
        Column(horizontalAlignment = Alignment.End, verticalArrangement = Arrangement.spacedBy(4.dp)) {
            val canOpenRoleDialog = canChangeRole && member.status == AgencyMemberStatus.Active && member.role != AgencyRole.Owner
            Row(
                modifier = Modifier
                    .clip(RoundedCornerShape(999.dp))
                    .background(if (canOpenRoleDialog) theme.primaryAccent.copy(alpha = 0.14f) else theme.surface.copy(alpha = 0.35f))
                    .border(
                        BorderStroke(1.dp, if (canOpenRoleDialog) theme.primaryAccent.copy(alpha = 0.72f) else theme.border),
                        RoundedCornerShape(999.dp)
                    )
                    .clickable(enabled = canOpenRoleDialog) { showRoleDialog = true }
                    .padding(horizontal = HavnSpacing.sm, vertical = 5.dp),
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    member.role.localizedDisplayName(strings),
                    color = if (canOpenRoleDialog) theme.primaryAccent else theme.secondaryText,
                    style = MaterialTheme.typography.labelLarge
                )
                if (canOpenRoleDialog) {
                    HavnIcon(
                        icon = HavnIconKey.Sliders,
                        contentDescription = strings.changeRole,
                        tint = theme.primaryAccent,
                        modifier = Modifier.size(14.dp)
                    )
                }
            }
            Text(member.status.displayName, color = theme.mutedText, style = MaterialTheme.typography.labelSmall)
            if (isUpdatingRole) {
                Text(strings.updating, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall)
            }
            if (canDeleteInvite && member.status == AgencyMemberStatus.Invited) {
                val invitationId = member.invitationId
                if (invitationId != null) {
                    TextButton(
                        onClick = { onDeleteInvite(invitationId) },
                        enabled = !isDeletingInvite
                    ) {
                        Text(
                            if (isDeletingInvite) strings.deleting else strings.deleteInvite,
                            color = theme.danger,
                            style = MaterialTheme.typography.labelMedium
                        )
                    }
                }
            }
        }
    }
    val memberAgentId = member.agentId
    if (showRoleDialog && memberAgentId != null) {
        AgencyChangeRoleDialog(
            memberName = member.fullName,
            currentRole = member.role,
            onDismiss = { showRoleDialog = false },
            onRoleSelected = { role ->
                showRoleDialog = false
                onUpdateRole(memberAgentId, role)
            },
            onRemoveAccess = {
                showRoleDialog = false
                onRemoveAccess(memberAgentId)
            }
        )
    }
}

@Composable
private fun AgencyChangeRoleDialog(
    memberName: String,
    currentRole: AgencyRole,
    onDismiss: () -> Unit,
    onRoleSelected: (AgencyRole) -> Unit,
    onRemoveAccess: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = theme.card,
        title = { Text(strings.changeRole, color = theme.primaryText, style = MaterialTheme.typography.titleLarge) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                Text(
                    "${strings.chooseAgencyRoleFor} $memberName.",
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
                listOf(AgencyRole.Admin, AgencyRole.Agent, AgencyRole.Assistant, AgencyRole.Viewer).forEach { option ->
                    AgencyRoleOption(
                        role = option,
                        selected = currentRole == option,
                        onClick = { onRoleSelected(option) }
                    )
                }
            }
        },
        confirmButton = {},
        dismissButton = {
            Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                TextButton(onClick = onRemoveAccess) {
                    Text(strings.removeAccess, color = theme.danger)
                }
                TextButton(onClick = onDismiss) {
                    Text(strings.commonCancel, color = theme.secondaryText)
                }
            }
        }
    )
}

@Composable
private fun AgencyInviteDialog(
    email: String,
    role: AgencyRole,
    isInviting: Boolean,
    onEmailChanged: (String) -> Unit,
    onRoleChanged: (AgencyRole) -> Unit,
    onDismiss: () -> Unit,
    onInvite: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = theme.card,
        title = { Text(strings.inviteAgent, color = theme.primaryText, style = MaterialTheme.typography.titleLarge) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                Text(
                    strings.inviteAgentBody,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
                HavnTextField(
                    value = email,
                    onValueChange = onEmailChanged,
                    label = strings.agentEmail,
                    leadingIcon = HavnIconKey.Mail,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email)
                )
                Text(strings.role, color = theme.mutedText, style = MaterialTheme.typography.labelLarge)
                Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                    listOf(AgencyRole.Admin, AgencyRole.Agent, AgencyRole.Assistant, AgencyRole.Viewer).forEach { option ->
                        AgencyRoleOption(
                            role = option,
                            selected = role == option,
                            onClick = { onRoleChanged(option) }
                        )
                    }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onInvite, enabled = !isInviting) {
                Text(if (isInviting) strings.inviting else strings.invite, color = theme.primaryAccent)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(strings.commonCancel, color = theme.secondaryText)
            }
        }
    )
}

@Composable
private fun AgencyRoleOption(
    role: AgencyRole,
    selected: Boolean,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val shape = RoundedCornerShape(14.dp)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(shape)
            .background(if (selected) theme.primaryAccent.copy(alpha = 0.14f) else theme.surface)
            .border(BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border), shape)
            .clickable(onClick = onClick)
            .padding(HavnSpacing.md),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
    ) {
        HavnIcon(
            icon = if (selected) HavnIconKey.Check else HavnIconKey.User,
            contentDescription = null,
            modifier = Modifier.size(18.dp),
            tint = if (selected) theme.primaryAccent else theme.secondaryText
        )
        Column {
            Text(role.localizedDisplayName(strings), color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
            Text(role.localizedShortDescription(strings), color = theme.secondaryText, style = MaterialTheme.typography.bodySmall)
        }
    }
}

@Composable
private fun AgencyLogo(
    name: String,
    iconUrl: String? = null,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Box(
        modifier = modifier
            .clip(CircleShape)
            .background(
                Brush.radialGradient(
                    colors = listOf(
                        theme.primaryAccent.copy(alpha = 0.2f),
                        theme.surface.copy(alpha = 0.92f)
                    )
                )
            )
            .border(1.dp, theme.primaryAccent.copy(alpha = 0.5f), CircleShape),
        contentAlignment = Alignment.Center
    ) {
        val rendered = HavnDatabaseImage(
            dataUrl = iconUrl,
            previewDataUrl = havnAgencyMediaPreviewSource(iconUrl, 96),
            contentDescription = null,
            modifier = Modifier.fillMaxSize(),
            contentScale = androidx.compose.ui.layout.ContentScale.Crop
        )
        if (!rendered) {
            Text(
                text = agencyInitials(name),
                color = theme.primaryAccent,
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Medium
            )
        }
    }
}

@Composable
private fun AgencyStatsCard(
    properties: Int,
    agents: Int,
    leads: Int
) {
    val strings = LocalHavnStrings.current
    HavnCard {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            AgencyStat(value = properties.toString(), label = strings.navProperties, modifier = Modifier.weight(1f))
            AgencyStatDivider()
            AgencyStat(value = agents.toString(), label = strings.agents, modifier = Modifier.weight(1f))
            AgencyStatDivider()
            AgencyStat(value = leads.toString(), label = strings.navLeads, modifier = Modifier.weight(1f))
        }
    }
}

@Composable
private fun AgencyStat(
    value: String,
    label: String,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        Text(value, color = theme.primaryText, style = MaterialTheme.typography.headlineSmall)
        Text(label, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
    }
}

@Composable
private fun AgencyStatDivider() {
    val theme = LocalHavnThemeTokens.current
    Box(
        modifier = Modifier
            .height(56.dp)
            .width(1.dp)
            .background(theme.border.copy(alpha = 0.7f))
    )
}

@Composable
private fun AgencyActivityCard(
    items: List<AgencyActivityItem>
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            items.forEachIndexed { index, item ->
                AgencyActivityRow(item)
                if (index != items.lastIndex) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(1.dp)
                            .background(theme.border.copy(alpha = 0.52f))
                    )
                }
            }
        }
    }
}

@Composable
private fun AgencyActivityRow(item: AgencyActivityItem) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
    ) {
        HavnIcon(
            icon = item.icon,
            contentDescription = null,
            modifier = Modifier.size(22.dp),
            tint = theme.primaryAccent
        )
        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.spacedBy(3.dp)
        ) {
            Text(item.title, color = theme.primaryText, style = MaterialTheme.typography.bodyMedium)
            Text(item.subtitle, color = theme.secondaryText, style = MaterialTheme.typography.bodySmall)
        }
        Text(item.time, color = theme.mutedText, style = MaterialTheme.typography.labelMedium)
    }
}

@Composable
private fun AgencyErrorDialog(
    error: String,
    onDismiss: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = theme.card,
        title = {
            Text(
                strings.commonAgency,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.SemiBold
            )
        },
        text = {
            Text(
                error,
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodyMedium
            )
        },
        confirmButton = {
            TextButton(onClick = onDismiss) {
                Text(strings.ok, color = theme.primaryAccent)
            }
        }
    )
}

private data class AgencyActivityItem(
    val icon: HavnIconKey,
    val title: String,
    val subtitle: String,
    val time: String
)

private fun agencyInitials(name: String): String {
    val parts = name
        .split(" ", "-", "_")
        .mapNotNull { part -> part.firstOrNull()?.uppercaseChar()?.toString() }
    return parts.take(2).joinToString("").ifBlank { "H" }
}

private fun String.initials(): String =
    trim()
        .split(" ")
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "A" }

private fun AgencyRole.localizedDisplayName(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    AgencyRole.Owner -> strings.agencyRoleOwner
    AgencyRole.Admin -> strings.agencyRoleAdmin
    AgencyRole.Agent -> strings.agencyRoleAgent
    AgencyRole.Assistant -> strings.agencyRoleAssistant
    AgencyRole.Viewer -> strings.agencyRoleViewer
}

private fun AgencyRole.localizedShortDescription(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    AgencyRole.Owner -> strings.agencyRoleOwnerDescription
    AgencyRole.Admin -> strings.agencyRoleAdminDescription
    AgencyRole.Agent -> strings.agencyRoleAgentDescription
    AgencyRole.Assistant -> strings.agencyRoleAssistantDescription
    AgencyRole.Viewer -> strings.agencyRoleViewerDescription
}

private fun AgencyRole.localizedCapabilities(strings: com.havn.app.core.localization.HavnStrings): List<String> = when (this) {
    AgencyRole.Owner -> listOf(
        strings.agencyCapabilityInviteRemoveMembers,
        strings.agencyCapabilityChangeRoles,
        strings.agencyCapabilityManageAllProperties,
        strings.agencyCapabilityAssignAgents,
        strings.agencyCapabilitySeeAllLeads,
        strings.agencyCapabilityExportReports,
        strings.agencyCapabilityChangeBranding,
        strings.agencyCapabilityDeleteAgency
    )
    AgencyRole.Admin -> listOf(
        strings.agencyCapabilityInviteRemoveMembers,
        strings.agencyCapabilityChangeRoles,
        strings.agencyCapabilityManageAllProperties,
        strings.agencyCapabilityAssignAgents,
        strings.agencyCapabilitySeeAllLeads,
        strings.agencyCapabilityExportReports,
        strings.agencyCapabilityChangeBranding
    )
    AgencyRole.Agent -> listOf(
        strings.agencyCapabilityViewAssignedProperties,
        strings.agencyCapabilityHostAssignedOpenings,
        strings.agencyCapabilitySeeAssignedLeads,
        strings.agencyCapabilityAddNotes,
        strings.agencyCapabilitySendFollowUps,
        strings.agencyCapabilityCreateSellerReports
    )
    AgencyRole.Assistant -> listOf(
        strings.agencyCapabilityViewAssignedProperties,
        strings.agencyCapabilityEditAssignedLeads,
        strings.agencyCapabilityPrepareFollowUps,
        strings.agencyCapabilityCreateDraftReports,
        strings.agencyCapabilityCannotDeleteProperties,
        strings.agencyCapabilityCannotInviteAgents
    )
    AgencyRole.Viewer -> listOf(
        strings.agencyCapabilityViewDashboards,
        strings.agencyCapabilityViewReports,
        strings.agencyCapabilityCannotEdit
    )
}

@Composable
private fun EntitlementCard(state: AgencyUiState) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Text(strings.currentPlan, color = theme.mutedText, style = MaterialTheme.typography.labelLarge)
            Text(
                state.entitlement.tier.databaseValue.toSentenceCaseLabel(),
                color = theme.primaryText,
                style = MaterialTheme.typography.headlineSmall
            )
            Text(
                "${strings.personalActivePropertyLimit}: ${state.entitlement.maxActivePersonalProperties}",
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodyMedium
            )
            Text(
                if (state.entitlement.isProAgent) {
                    strings.agencyToolsAvailable
                } else {
                    strings.personalAccountAgencyToolsLocked
                },
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodyMedium
            )
        }
    }
}

@Composable
private fun AgencyCard(
    agency: Agency,
    selected: Boolean,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard(modifier = Modifier.clickable(onClick = onClick)) {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Row(horizontalArrangement = Arrangement.SpaceBetween, modifier = Modifier.fillMaxWidth()) {
                Text(agency.name, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                Text(
                    if (selected) strings.selected else agency.role.localizedDisplayName(strings),
                    color = theme.mutedText,
                    style = MaterialTheme.typography.labelMedium
                )
            }
            Text("${agency.memberCount} ${strings.activeMembers}", color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            Text(
                strings.agencySharedWorkspaceBody,
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodyMedium
            )
        }
    }
}

private fun String.toSentenceCaseLabel(): String =
    replace("_", " ")
        .lowercase()
        .replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
